import React, {useEffect, useState} from "react";
import {Alert, Button, Form, Modal, Spinner} from "react-bootstrap";
import AdhocImageUpload from "../AdhocImageUpload";
import AdhocImageGallary from "../AdhocImageGallary";
import {getJobComplaint, normalizeComplaint, saveJobComplaint} from "../../service/JobComplaintService";

const emptyComplaint = job => ({
    cosJobId: job.cos_job_id,
    reason: "",
    deductionAmount: 0,
    images: [],
});

export default function JobComplaintModal({job, show, onClose, onSaved, onSaveComplaint, sourceExtras}) {
    const [complaint, setComplaint] = useState(() => emptyComplaint(job));
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        getJobComplaint(job.cos_job_id)
            .then(async response => {
                if (!active) return;
                if (response.status === 404) {
                    setComplaint(emptyComplaint(job));
                    return;
                }
                if (!response.ok) throw new Error(await response.text() || "Unable to load complaint");
                const data = normalizeComplaint(await response.json());
                if (active) setComplaint({...data, images: data.images || []});
            })
            .catch(err => active && setError(err.message))
            .finally(() => active && setLoading(false));
        return () => { active = false; };
    }, [job]);

    async function handleSave(event) {
        event.preventDefault();
        if (loading || saving || uploading) return;
        if (!complaint.reason.trim()) {
            setError("Complaint reason is required.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            if (onSaveComplaint) {
                const saved = await onSaveComplaint({...complaint, reason: complaint.reason.trim()});
                setComplaint(normalizeComplaint(saved));
                onSaved?.(saved);
                onClose();
                return;
            }
            const response = await saveJobComplaint({...complaint, reason: complaint.reason.trim()});
            if (!response.ok) throw new Error(await response.text() || "Unable to save complaint");
            setComplaint(normalizeComplaint(await response.json()));
            onSaved?.();
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    }

    const closeWhenIdle = () => { if (!uploading && !saving) onClose(); };

    return <Modal show={show} onHide={closeWhenIdle} backdrop={uploading || saving ? "static" : true} keyboard={!uploading && !saving} size="lg" onClick={event => event.stopPropagation()}>
        <Form aria-label="Complaint form" onSubmit={handleSave}>
            <Modal.Header closeButton={!uploading && !saving}><Modal.Title>Job Complaint</Modal.Title></Modal.Header>
            <Modal.Body>
                <p><b>Job:</b> {job.cos_job_id} · {job.full_address} · {job.date}</p>
                {error ? <Alert variant="danger">{error}</Alert> : null}
                {loading ? <div className="text-center"><Spinner animation="border"/></div> : <fieldset disabled={saving}>
                    <Form.Group className="mb-3" controlId="complaint-reason">
                        <Form.Label>Complaint reason</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            maxLength={2000}
                            required
                            value={complaint.reason}
                            onChange={event => setComplaint({...complaint, reason: event.target.value})}
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="complaint-deduction">
                        <Form.Label>Wage deduction ($)</Form.Label>
                        <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            value={complaint.deductionAmount}
                            onChange={event => setComplaint({...complaint, deductionAmount: event.target.value})}
                        />
                        <Form.Text>This is separate from the Job Credit. Enter 0 when no wage deduction is required.</Form.Text>
                    </Form.Group>
                    {sourceExtras ? sourceExtras({saving, uploading, complaint}) : null}
                    <AdhocImageUpload
                        accept="image/*"
                        images={complaint.images}
                        setImages={images => setComplaint(current => ({...current, images}))}
                        onUploadingChange={setUploading}
                        onUploadError={setError}
                    />
                    <AdhocImageGallary
                        images={complaint.images}
                        setImages={images => setComplaint(current => ({...current, images}))}
                        isReadOnly={uploading || saving}
                    />
                </fieldset>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeWhenIdle} disabled={uploading || saving}>Close</Button>
                <Button type="submit" disabled={loading || saving || uploading}>{uploading ? "Uploading photos…" : saving ? "Saving…" : "Save complaint"}</Button>
            </Modal.Footer>
        </Form>
    </Modal>;
}
